/**
 * Peças compartilhadas das telas de configuração no padrão TiFlux:
 * breadcrumb "Configurações / <Seção> / <Tela>", busca client-side e
 * normalização de texto sem acento/caixa para os filtros.
 */
import { Link, type LinkProps } from '@tanstack/react-router';
import { Search } from 'lucide-react';

/** Busca client-side sem sensibilidade a acento/caixa. */
export function normalizeText(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** `true` quando `haystack` contém `query` já normalizada. */
export function matchesText(haystack: string, normalizedQuery: string): boolean {
  return normalizeText(haystack).includes(normalizedQuery);
}

/**
 * Breadcrumb "Configurações / <Seção> [/ <Intermediário>] / <Tela>" —
 * "Configurações" linka o hub /settings; `parent` é um segmento intermediário
 * COM link (ex.: a lista de mesas antes do detalhe de uma mesa); o restante é
 * texto (a tela atual em destaque). `section={null}` omite a seção (telas sem
 * seção no TiFlux, ex.: Grupo de tickets).
 */
export function SettingsBreadcrumb({
  section = 'Mesa de serviço',
  parent,
  page,
}: {
  section?: string | null;
  parent?: { label: string; to: LinkProps['to'] };
  page: string;
}) {
  return (
    <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
      <Link to="/settings" className="hover:text-foreground">
        Configurações
      </Link>
      <span className="mx-1.5">/</span>
      {section !== null ? (
        <>
          <span>{section}</span>
          <span className="mx-1.5">/</span>
        </>
      ) : null}
      {parent ? (
        <>
          <Link to={parent.to} className="hover:text-foreground">
            {parent.label}
          </Link>
          <span className="mx-1.5">/</span>
        </>
      ) : null}
      <span className="font-medium text-foreground">{page}</span>
    </nav>
  );
}

/** Campo de busca com lupa à direita, como nas listas do TiFlux. */
export function SettingsSearch({
  value,
  onChange,
  label = 'Buscar',
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}) {
  return (
    <div className="relative">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Buscar"
        aria-label={label}
        className="h-9 w-48 rounded-md border border-border bg-card pl-3 pr-8 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary sm:w-64"
      />
      <Search className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
    </div>
  );
}
